import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { readCookieConsent, saveCookieConsent } from "@/lib/cookieConsent";

type DraftConsent = {
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
};

const defaultDraft: DraftConsent = {
  analytics: false,
  marketing: false,
  preferences: false,
};

const CookieConsentBanner = () => {
  const [loaded, setLoaded] = useState(false);
  const [hasSavedConsent, setHasSavedConsent] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draft, setDraft] = useState<DraftConsent>(defaultDraft);

  useEffect(() => {
    const current = readCookieConsent();
    if (current) {
      setHasSavedConsent(true);
      setDraft({
        analytics: current.analytics,
        marketing: current.marketing,
        preferences: current.preferences,
      });
    }
    setLoaded(true);
  }, []);

  useEffect(() => {
    const openPreferences = () => {
      const current = readCookieConsent();
      setDraft(
        current
          ? {
              analytics: current.analytics,
              marketing: current.marketing,
              preferences: current.preferences,
            }
          : defaultDraft
      );
      setDialogOpen(true);
    };
    window.addEventListener("open-cookie-preferences", openPreferences);
    return () => window.removeEventListener("open-cookie-preferences", openPreferences);
  }, []);

  const showBanner = useMemo(() => loaded && !hasSavedConsent, [loaded, hasSavedConsent]);

  const acceptAll = () => {
    saveCookieConsent({ analytics: true, marketing: true, preferences: true });
    setHasSavedConsent(true);
    setDialogOpen(false);
  };

  const rejectOptional = () => {
    saveCookieConsent({ analytics: false, marketing: false, preferences: false });
    setHasSavedConsent(true);
    setDialogOpen(false);
  };

  const saveCustom = () => {
    saveCookieConsent(draft);
    setHasSavedConsent(true);
    setDialogOpen(false);
  };

  return (
    <>
      {showBanner && (
        <div className="fixed bottom-4 left-4 right-4 z-[70]">
          <div className="mx-auto max-w-6xl rounded-xl border border-slate-200 bg-white/95 p-4 shadow-xl backdrop-blur supports-[backdrop-filter]:bg-white/80 sm:p-5 lg:p-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="space-y-1 lg:max-w-[62%]">
                <p className="text-sm font-semibold text-slate-900">Cookies and tracking preferences</p>
                <p className="text-xs text-slate-600 sm:text-sm">
                  We use necessary cookies for core site functionality and optional cookies for analytics, preferences,
                  and marketing.
                </p>
              </div>
              <div className="grid w-full grid-cols-1 gap-2 sm:grid-cols-2 lg:flex lg:w-auto lg:flex-wrap lg:justify-end">
                <Button className="w-full lg:w-auto" variant="outline" size="sm" onClick={() => setDialogOpen(true)}>
                  Customize
                </Button>
                <Button className="w-full lg:w-auto" variant="outline" size="sm" onClick={rejectOptional}>
                  Reject non-essential
                </Button>
                <Button className="w-full sm:col-span-2 lg:w-auto lg:col-span-1" size="sm" onClick={acceptAll}>
                  Accept all
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Cookie preferences</DialogTitle>
            <DialogDescription>
              Necessary cookies are always on. Choose which optional cookies you allow.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 text-sm">
            <label className="flex items-start justify-between gap-3 rounded-lg border border-slate-200 p-3">
              <div>
                <p className="font-medium text-slate-900">Necessary</p>
                <p className="text-xs text-slate-500">Required for security, authentication, and core navigation.</p>
              </div>
              <input type="checkbox" checked readOnly className="mt-1 h-4 w-4 accent-primary" />
            </label>

            <label className="flex items-start justify-between gap-3 rounded-lg border border-slate-200 p-3">
              <div>
                <p className="font-medium text-slate-900">Analytics</p>
                <p className="text-xs text-slate-500">Helps us measure usage and improve the site.</p>
              </div>
              <input
                type="checkbox"
                checked={draft.analytics}
                onChange={(e) => setDraft((prev) => ({ ...prev, analytics: e.target.checked }))}
                className="mt-1 h-4 w-4 accent-primary"
              />
            </label>

            <label className="flex items-start justify-between gap-3 rounded-lg border border-slate-200 p-3">
              <div>
                <p className="font-medium text-slate-900">Preferences</p>
                <p className="text-xs text-slate-500">Stores UI and experience preferences.</p>
              </div>
              <input
                type="checkbox"
                checked={draft.preferences}
                onChange={(e) => setDraft((prev) => ({ ...prev, preferences: e.target.checked }))}
                className="mt-1 h-4 w-4 accent-primary"
              />
            </label>

            <label className="flex items-start justify-between gap-3 rounded-lg border border-slate-200 p-3">
              <div>
                <p className="font-medium text-slate-900">Marketing</p>
                <p className="text-xs text-slate-500">Used for campaign attribution and remarketing.</p>
              </div>
              <input
                type="checkbox"
                checked={draft.marketing}
                onChange={(e) => setDraft((prev) => ({ ...prev, marketing: e.target.checked }))}
                className="mt-1 h-4 w-4 accent-primary"
              />
            </label>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={rejectOptional}>
              Reject non-essential
            </Button>
            <Button variant="outline" onClick={acceptAll}>
              Accept all
            </Button>
            <Button onClick={saveCustom}>Save choices</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CookieConsentBanner;
